// FEX Inbound / Live Transfer interest requests.
//
// Agents submit from the Lead Center. Each request is stored in Supabase
// (public.inbound_requests) in a 'pending' state and shows up in the admin
// panel, where an admin approves or declines it. A copy is also POSTed to the
// INBOUND_REQUESTS Make.com webhook when one is configured (non-blocking).

import { supabase } from '../lib/supabase';
import { createNotification } from './notifications';
import { WEBHOOK_URLS } from '../config/webhooks';

export type DayMode = 'all-day' | 'range' | 'closed';
export type InboundRequestStatus = 'pending' | 'approved' | 'declined';

export interface DaySchedule {
  mode: DayMode;
  /** 24h "HH:MM"; only meaningful when mode === 'range' */
  start: string;
  end: string;
}

export interface InboundRequestData {
  agentId: string | null;
  productName: string;
  productSlug: string;
  unitLabel: string;
  pricePerUnit: number;
  packageSize: number;
  fullName: string;
  email: string;
  phone: string;
  states: string[];
  specialInstructions: string;
  dailyCap: string;
  schedule: Record<string, DaySchedule>;
}

export interface InboundRequest {
  id: string;
  created_at: string;
  updated_at: string;
  agent_id: string | null;
  product_name: string;
  product_slug: string;
  unit_label: string;
  price_per_unit: number | null;
  package_size: number | null;
  full_name: string;
  email: string;
  phone: string;
  states: string;
  state_count: number;
  daily_cap: string | null;
  schedule: Record<string, DaySchedule>;
  special_instructions: string | null;
  status: InboundRequestStatus;
  admin_notes: string | null;
  reviewed_at: string | null;
}

// Fire-and-forget copy to the Make.com webhook (if configured).
async function mirrorToWebhook(data: InboundRequestData) {
  const webhookUrl = WEBHOOK_URLS.INBOUND_REQUESTS;
  if (!webhookUrl) return;
  try {
    // text/plain avoids a CORS preflight; Make.com parses the JSON body fine.
    await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        ...data,
        states: data.states.join(', '),
        stateCount: data.states.length,
        source: 'enhance-lead-center',
        type: 'fex-inbound-request',
      }),
    });
  } catch (error) {
    console.error('[inbound-request] Webhook mirror failed (non-critical):', error);
  }
}

// Agent: submit a request. Stored as 'pending' for admin review.
export async function submitInboundRequest(
  data: InboundRequestData
): Promise<{ success: boolean; id?: string; error?: string }> {
  try {
    const { data: row, error } = await supabase
      .from('inbound_requests')
      .insert({
        agent_id: data.agentId,
        product_name: data.productName,
        product_slug: data.productSlug,
        unit_label: data.unitLabel,
        price_per_unit: data.pricePerUnit,
        package_size: data.packageSize,
        full_name: data.fullName,
        email: data.email,
        phone: data.phone,
        states: data.states.join(', '),
        state_count: data.states.length,
        daily_cap: data.dailyCap || null,
        schedule: data.schedule,
        special_instructions: data.specialInstructions || null,
        status: 'pending',
      })
      .select('id')
      .single();

    if (error) {
      console.error('[inbound-request] Insert failed:', error);
      return { success: false, error: 'Failed to submit request. Please try again.' };
    }

    // Non-blocking mirror to the webhook automation.
    mirrorToWebhook(data);

    return { success: true, id: row.id };
  } catch (error) {
    console.error('[inbound-request] Submission error:', error);
    return { success: false, error: 'Something went wrong. Please try again.' };
  }
}

// Agent: list own requests (to see pending/approved/declined status).
export async function getAgentInboundRequests(
  agentId: string
): Promise<{ success: boolean; data?: InboundRequest[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('inbound_requests')
      .select('*')
      .eq('agent_id', agentId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return { success: true, data: (data ?? []) as InboundRequest[] };
  } catch (error: any) {
    console.error('Error fetching agent inbound requests:', error);
    return { success: false, error: error.message ?? 'Failed to fetch requests' };
  }
}

// Admin: list all requests.
export async function getAllInboundRequests(): Promise<{ success: boolean; data?: InboundRequest[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('inbound_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return { success: true, data: (data ?? []) as InboundRequest[] };
  } catch (error: any) {
    console.error('Error fetching inbound requests:', error);
    return { success: false, error: error.message ?? 'Failed to fetch requests' };
  }
}

// Admin: approve or decline a request. Notifies the agent so they know their
// request moved out of the pending state.
export async function updateInboundRequestStatus(
  id: string,
  status: InboundRequestStatus,
  adminNotes?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('inbound_requests')
      .update({
        status,
        admin_notes: adminNotes ?? null,
        reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('agent_id, product_name, package_size, unit_label')
      .single();
    if (error) throw error;

    // Let the agent know the outcome.
    if (data?.agent_id && status !== 'pending') {
      const label = `${data.package_size ?? ''} ${data.product_name}`.trim();
      const approved = status === 'approved';
      createNotification(
        data.agent_id,
        approved ? 'inbound_request_approved' : 'inbound_request_declined',
        approved ? 'Inbound Request Approved' : 'Inbound Request Update',
        approved
          ? `Your request for ${label} was approved. Our team will reach out to get you set up.`
          : `Your request for ${label} was declined. Reach out to our team if you have questions.`,
        { inbound_request_id: id }
      );
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error updating inbound request:', error);
    return { success: false, error: error.message ?? 'Failed to update request' };
  }
}
